<script setup lang="ts">
import UserListItem from "@/components/user/userListItem.vue";
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import { requiredValidator } from "@validators";
import { isEmpty } from "lodash";
import { useTheme } from "vuetify";
import { VForm } from "vuetify/components/VForm";
import UserChip from "@/components/user/userChips.vue";

const props = defineProps<{
  form: any;
  activityList: any[];
  areaList: any[];
  areaPlural?: string;
  areaSingular?: string;
  memberList: any[];
  isEditEvent: boolean;
  haveEditAccess: boolean;
  haveCreateAccess: boolean;
  isDrawerOpen: boolean;
  bookingsActualDuration: number;
  setActualDuration: number;
}>();

const emit = defineEmits<{
  fetchPayment: [];
  "update:setActualDuration": [value: number];
  addActualDuration: [value: number];
}>();

const formRef = ref<InstanceType<typeof VForm>>();
const vuetifyTheme = useTheme();

const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const availableTimes = computed(() =>
  props.activityList?.reduce((times: any[], activity: any) => {
    return props.form.activity?.[activity?.id]
      ? [...times, ...(activity?.expand?.availibleTimes || [])]
      : times;
  }, [])
);

const timeList = computed(() => {
  const times: any[] = [];
  if (!props.form.date || isEmpty(props.form?.activity)) return [];
  if (!props.haveEditAccess && !props.haveCreateAccess) return [];

  const interval = 30;
  const now = moment();
  const selectedDate = moment(props.form?.date);
    const isToday = selectedDate.isSame(moment(),"day");

  const filterPastTimes = (hour: number, min: number) => {
    if (isToday) {
      return hour > now.hour() || (hour === now.hour() && min >= now.minute());
    }
    return true;
  };

  const formatTimeTo12Hour = (hour: number, min: number) =>
    moment(`${hour}:${min}`, "HH:mm").format("hh:mm A");

  const dayName = props.form.date
    ? moment.utc(props.form.date)?.format("dddd").toLowerCase()
    : "sunday";

  const filteredTimes = availableTimes.value?.filter((timeSlot: any) => timeSlot[dayName]);

  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += interval) {
      if (!filterPastTimes(hour, min)) continue;

      const formattedHour = hour.toString().padStart(2, "0");
      const formattedMin = min.toString().padStart(2, "0");
      const time = `${formattedHour}:${formattedMin}`;
      const timeInMinutes = hour * 60 + min;

      const isAvailable = filteredTimes.some((timeSlot: any) => {
        const startTime = timeSlot.startHour * 60 + timeSlot.startMin;
        const endTime = timeSlot.finishHour * 60 + timeSlot.finishMin;
        return timeInMinutes >= startTime && timeInMinutes < endTime;
      });

      times.push({
        label: formatTimeTo12Hour(hour, min),
        value: time,
        color: !isAvailable
          ? vuetifyTheme.name.value === "dark" ? "rgb(72, 72, 72)" : "lightgrey"
          : "",
        isNormalHour: isAvailable,
      });
    }
  }
  return times;
});

const allowedDates = (date: any) => {
  const day = moment(date);
  return day.isSameOrAfter(moment().startOf('day'));
};

const handleMenuUpdate = (isOpen: boolean) => {
  if (isOpen) {
    setTimeout(() => {
      const overlayContainer = document?.querySelector(".select-menu-overlay");
      const list = overlayContainer?.querySelector(".v-list");
      if (list) {
        const selectedEl = Array.from(list.children).find((el) =>
          el.classList.contains("selected")
        );
        const normalEl = Array.from(list.children).find((el) =>
          el.classList.contains("is-normal-hour")
        );
        (selectedEl ?? normalEl)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 200);
  }
};

const selectBays = async (bayId: string, activityId: string) => {
  if (!bayId || !activityId) return;
  if (!props.form.activity) props.form.activity = {};
  if (!Array.isArray(props.form.activity[activityId])) props.form.activity[activityId] = [];

  const activityBays = props.form.activity[activityId];
  const index = activityBays.indexOf(bayId);
  if (index === -1) activityBays.push(bayId);
  else activityBays.splice(index, 1);
  if (activityBays.length === 0) delete props.form.activity[activityId];

  if (props.isDrawerOpen && props.form.date && props.form.time && props.form.duration) {
    emit("fetchPayment");
  }
};

defineExpose({
  validate: () => formRef.value?.validate(),
});
</script>

<template>
  <VForm ref="formRef">
    <VWindowItem class="mt-2">
      <VRow class="pa-0">
        <VCol sm="6" cols="12" class="pa-0 pl-4 d-flex align-center">
          <span>Choose the booking {{ props?.areaPlural }}, Time, and Duration.</span>
        </VCol>
        <VCol sm="6" cols="12" class="pa-0 pr-3">
          <div class="float-right">
            <VCheckbox
              v-if="!form.trackTickets"
              v-model="form.isRecurring"
              :readonly="isEditEvent"
              label="Make Recurring"
            />
            <VCheckbox
              v-if="!form.isRecurring"
              v-model="form.trackTickets"
              label="Track Tickets"
            />
          </div>
        </VCol>
      </VRow>
      <VRow>
        <VCol md="3" sm="5" cols="4">
          <div v-for="activity in activityList" :key="activity.id" class="pa-3">
            <div class="d-flex align-items-center">
              <div class="font-weight-medium text-primary">
                {{ activity.name }} {{ areaPlural }}
              </div>
            </div>
          </div>
        </VCol>
        <VCol md="5" sm="7" cols="8">
          <div v-for="activity in activityList" :key="activity.id" class="pb-1 pt-1">
            <div class="d-flex align-center gap-2 flex-wrap">
              <VBtn
                v-for="area in areaList?.filter((area) => area.activities.includes(activity.id))"
                icon=""
                :key="area.id"
                :color="form?.activity[activity.id]?.includes(area?.id) ? 'primary' : 'grey-400'"
                size="small"
                class="rounded"
                rounded
                @click="selectBays(area?.id, activity.id)"
              >
                {{ area?.areaNumber }}
                <VTooltip activator="parent" location="top" open-delay="500">
                  {{ `${areaSingular} ${area?.areaNumber}` }}
                </VTooltip>
              </VBtn>
            </div>
          </div>
        </VCol>

        <VCol md="4" cols="12" class="d-flex flex-column flex-sm-row flex-md-column space-md-between gap-4">
          <div class="w-100 w-md-33 w-md-100">
            <DateInput
              v-model="form.date"
              label="Date"
              class="searchDate"
              :rules="[requiredValidator]"
              format="dddd, Do MMM YYYY"
              :allowed-dates="allowedDates"
            />
          </div>
          
          <div class="w-100 w-md-33 w-md-100">
            <VAutocomplete
            v-model="form.owner"
            :items="props?.memberList"
            :rules="[requiredValidator]"
            chips
            closable-chips
            item-title="firstName"
            item-value="id"
            color="blue-grey-lighten-2"
            label="Owner"
            class="member_list_selection"
          >
            <template v-slot:chip="{ props, item }">
              <UserChip
                :bind="props"
                :item="item"
                :baseUrl="pb.baseUrl"
                @removeUser="form.owner = []"
              />
            </template>

            <template v-slot:item="{ props, item }">
              <UserListItem
                :bind="props"
                :item="item"
                :baseUrl="pb.baseUrl"
              />
            </template>
            <template v-slot:selection="{ item }">
              <span>
                {{
                  item.firstName && item.lastName
                    ? `${item.firstName} ${item.lastName}`
                    : item.username
                }}
              </span>
            </template>
          </VAutocomplete>
          </div>

          
        </VCol>
      </VRow>
      <VRow>
        <VCol sm="4" cols="6">
          <VSelect
            v-model="form.time"
            :items="timeList"
            :rules="[requiredValidator]"
            :menu-props="{ contentClass: 'select-menu-overlay' }"
            item-title="label"
            item-value="value"
            label="Booked Time"
            placeholder="Select Time"
            class="dynamic_time_list"
            @update:menu="handleMenuUpdate"
          >
            <template v-slot:item="{ props: itemProps, item }">
              <VListItem
                v-bind="itemProps"
                :ref="item.value === form.time ? 'selectedItem' : undefined"
                :class="{
                  'is-normal-hour': item.raw.isNormalHour,
                  dark_theme: $vuetify.theme.current.dark,
                  light_theme: !$vuetify.theme.current.dark,
                  restricted_hours: !item.raw.isNormalHour,
                  selected: item.value === form.time,
                }"
                :style="{
                  backgroundColor: item.raw.color,
                  color: $vuetify.theme.current.dark ? '#fff' : '',
                }"
              />
            </template>
          </VSelect>
        </VCol>

        <VCol md="3" sm="4" cols="6">
          <VCombobox
            v-model="form.duration"
            :items="[1, 2, 3, 4]"
            :rules="[requiredValidator]"
            label="Duration"
            placeholder="Select Duration"
          />
        </VCol>

        <VCol md="5" sm="6" cols="12">
          <VCombobox
            :model-value="form.visitorEmail"
            @update:modelValue="(val) => form.visitorEmail = typeof val === 'object' && val !== null ? val.email : val"
            :items="memberList"
            :rules="[requiredValidator]"
            label="Notify Email"
            placeholder="Select user or enter in email"
            clearable
            item-title="email"
            item-value="email"
          >
            <template v-slot:item="{ props: itemProps, item }">
              <UserListItem :bind="itemProps" :item="item" :baseUrl="pb.baseUrl" />
            </template>
            <template v-slot:selection="{ item }">
              {{ item.raw.email || item.value }}
            </template>
          </VCombobox>
        </VCol>

        <VCol v-if="bookingsActualDuration" cols="12">
          <VAlert>
            <div class="pt-2 pb-2 d-flex">
              <VSelect
                density="compact"
                label="Update Actual Duration"
                :items="[
                  setActualDuration - 30, setActualDuration - 25, setActualDuration - 20,
                  setActualDuration - 15, setActualDuration - 10, setActualDuration - 5,
                  setActualDuration,
                  setActualDuration + 5, setActualDuration + 10, setActualDuration + 15,
                  setActualDuration + 20, setActualDuration + 25, setActualDuration + 30,
                ]"
                :model-value="setActualDuration"
                @update:model-value="emit('update:setActualDuration', $event)"
              />
              <VBtn
                color="primary"
                class="ms-5"
                @click.prevent="emit('addActualDuration', setActualDuration)"
              >
                Update Actual Duration
              </VBtn>
            </div>
          </VAlert>
        </VCol>
      </VRow>
    </VWindowItem>
  </VForm>
</template>
